import { create } from 'zustand'
import { devtools } from 'zustand/middleware'
import {
  SessionStatus,
  SessionNodeStatus,
  SessionStateDto,
  SessionNodeDto,
  PendingInputDto,
  VotingStateDto,
  JournalEntryDto,
  SessionInputType,
  QuestionAnswerChallengeDto,
  NumpadChallengeDto,
  QrCodeChallengeDto,
  ModeratorApprovalChallengeDto,
  CompletionSummaryDto,
  SessionParticipantInfo,
  SessionVotingSettings,
} from '@api/types/session'

// Map numeric voting round status from API to string expected by VotingStateDto
const VOTING_STATUS_MAP: Record<number, VotingStateDto['status']> = {
  0: 'Open',
  1: 'Closed',
  2: 'Resolved',
}

/**
 * Normalize raw activeVotingRound from API into VotingStateDto.
 * The API returns fields like `id`, numeric `status`, `workflowNodeId`, and no `options`,
 * whereas the frontend expects `roundId`, string `status`, `nodeId`, and `options[]`.
 * Challenge answer options are mapped into voting options when present.
 */
function normalizeVotingRound(
  raw: any,
  challengeOptions?: Array<{ id: string; text: string }> | null,
): VotingStateDto | null {
  if (!raw) return null

  // If already normalized (has roundId and string status), return as-is
  if (raw.roundId && typeof raw.status === 'string') {
    return raw as VotingStateDto
  }

  const totalVoters = raw.totalVoters ?? raw.TotalVoters ?? 0
  const votesCast = raw.votesCast ?? raw.VotesCast ?? 0
  const rawStatus = raw.status ?? raw.Status
  const status = typeof rawStatus === 'number'
    ? (VOTING_STATUS_MAP[rawStatus] ?? 'Closed')
    : (rawStatus ?? 'Closed')

  // Build options from challenge answer options if not present on the voting round
  const rawOptions = raw.options ?? raw.Options
  let options: VotingStateDto['options'] = []
  if (Array.isArray(rawOptions) && rawOptions.length > 0) {
    options = rawOptions.map((o: any) => ({
      id: o.id ?? o.Id,
      label: o.label ?? o.Label ?? o.text ?? o.Text,
      voteCount: o.voteCount ?? o.VoteCount,
    }))
  } else if (Array.isArray(challengeOptions) && challengeOptions.length > 0) {
    options = challengeOptions.map((o) => ({
      id: o.id,
      label: o.text,
    }))
  }

  return {
    roundId: raw.roundId ?? raw.RoundId ?? raw.id ?? raw.Id,
    nodeId: raw.nodeId ?? raw.NodeId ?? raw.workflowNodeId ?? raw.WorkflowNodeId,
    status,
    options,
    userVote: raw.userVote ?? raw.UserVote ?? null,
    totalVoters,
    votesCast,
    requiredVotes: raw.requiredVotes ?? raw.RequiredVotes ?? totalVoters,
    timerExpiresAt: raw.timerExpiresAt ?? raw.TimerExpiresAt ?? null,
    percentageVoted: raw.percentageVoted ?? raw.PercentageVoted ?? (totalVoters > 0 ? Math.round((votesCast / totalVoters) * 100) : 0),
    isComplete: raw.isComplete ?? raw.IsComplete ?? (status === 'Resolved'),
  }
}

// POI location revealed during play
export interface RevealedPoiLocation {
  nodeId: string
  nodeLabel: string
  latitude: number
  longitude: number
  description?: string
  markerEmoji?: string
  markerStyle?: 'icon' | 'circle' | 'none'
  circleRadius?: number
  circleColor?: string
  circleBorderStyle?: 'solid' | 'dashed' | 'none'
  circleFillStyle?: 'solid' | 'gradient'
  revealedAt: string
}

// Route revealed during play
export interface RevealedRoute {
  nodeId: string
  nodeLabel?: string
  routePoints: [number, number][]  // Array of [lat, lng] coordinates
  lineColor: string
  lineStyle: 'solid' | 'dashed' | 'dotted'
  lineOpacity: number
  arrowStyle: 'none' | 'end' | 'along' | 'animated'
  travelMode?: string
  distanceMeters?: number
  durationSeconds?: number
  revealedAt: string
}

// ====================
// STATE INTERFACE
// ====================

export interface SessionState {
  // Core session data
  sessionId: string | null
  adventureId: string | null
  adventureTitle: string | null
  status: SessionStatus

  // Current node
  currentNodeId: string | null
  currentNode: SessionNodeDto | null
  currentNodeStatus: SessionNodeStatus

  // Cooldown state
  cooldownEndsAt: string | null
  waitingReason: string | null

  // Input requirements
  requiresInput: boolean
  inputType: SessionInputType | null
  pendingInput: PendingInputDto | null

  // Progress
  progressPercent: number
  startedAt: string | null
  completedAt: string | null
  lastActivityAt: string | null
  elapsedSeconds: number

  // Group session
  isSolo: boolean
  isGroup: boolean
  groupId: string | null
  participantCount: number

  // Voting
  isVotingActive: boolean
  voting: VotingStateDto | null
  votingQuestionText: string | null
  votingChallengeType: SessionInputType | null
  votingChallengeData: Record<string, unknown> | null

  // Group participants
  participants: SessionParticipantInfo[]
  isSessionOwner: boolean

  // Journal
  journal: JournalEntryDto[]

  // Revealed POI locations for map
  revealedLocations: RevealedPoiLocation[]

  // Revealed routes for map
  revealedRoutes: RevealedRoute[]

  // Linked chat conversation
  conversationId: string | null

  // Voting settings (for group session lobby)
  votingSettings: SessionVotingSettings | null

  // Journal read tracking
  latestJournalSequenceNumber: number

  // Proximity
  proximityWarning: string | null

  // Clue state
  hasAvailableClues: boolean
  cluesUsed: number
  totalCluePenaltySeconds: number

  // Timer control
  effectiveDuration: string | null
  totalPausedSeconds: number
  isTimerPaused: boolean

  // Completion summary (set when session reaches terminal state)
  completionSummary: CompletionSummaryDto | null

  // UI state
  isLoading: boolean
  isProcessing: boolean
  isSubmitting: boolean
  error: string | null
  showJournal: boolean
  showMap: boolean

  // Debug - last raw API response (stored as unknown to preserve all fields)
  lastStateResponse: unknown
  lastStateResponseTimestamp: string | null

  // Actions
  loadSession: (state: SessionStateDto) => void
  updateSessionState: (state: Partial<SessionStateDto>) => void
  setCurrentNode: (node: SessionNodeDto | null) => void
  setPendingInput: (input: PendingInputDto | null) => void
  setStatus: (status: SessionStatus) => void

  // Journal actions
  setJournal: (entries: JournalEntryDto[]) => void
  addJournalEntry: (entry: JournalEntryDto) => void
  addJournalEntries: (entries: JournalEntryDto[]) => void

  // Map actions
  addRevealedLocation: (location: RevealedPoiLocation) => void
  clearRevealedLocations: () => void
  addRevealedRoute: (route: RevealedRoute) => void
  clearRevealedRoutes: () => void

  // Participant actions
  setParticipants: (participants: SessionParticipantInfo[]) => void
  addParticipant: (participant: SessionParticipantInfo) => void
  removeParticipant: (userId: string) => void
  updateParticipantOnlineStatus: (userId: string, isOnline: boolean) => void
  updateParticipantReady: (userId: string, isReady: boolean, readyAt: string | null) => void
  setIsSessionOwner: (isOwner: boolean) => void

  // Voting actions
  setVoting: (voting: VotingStateDto | null) => void
  updateVotingProgress: (votesCast: number, percentageVoted: number, isComplete?: boolean) => void

  // Completion summary
  setCompletionSummary: (summary: CompletionSummaryDto | null) => void

  // Clue actions
  setClueState: (hasAvailable: boolean, used: number, penalty: number) => void

  // UI actions
  setLoading: (loading: boolean) => void
  setProcessing: (processing: boolean) => void
  setSubmitting: (submitting: boolean) => void
  setError: (error: string | null) => void
  toggleJournal: () => void
  toggleMap: () => void
  setShowJournal: (show: boolean) => void
  setShowMap: (show: boolean) => void

  // Debug actions
  setLastStateResponse: (response: unknown) => void

  // Reset
  reset: () => void
}

// ====================
// INITIAL STATE
// ====================

const initialState = {
  // Core session data
  sessionId: null,
  adventureId: null,
  adventureTitle: null,
  status: SessionStatus.NotStarted,

  // Current node
  currentNodeId: null,
  currentNode: null,
  currentNodeStatus: SessionNodeStatus.Pending,

  // Cooldown state
  cooldownEndsAt: null,
  waitingReason: null,

  // Input requirements
  requiresInput: false,
  inputType: null,
  pendingInput: null,

  // Progress
  progressPercent: 0,
  startedAt: null,
  completedAt: null,
  lastActivityAt: null,
  elapsedSeconds: 0,

  // Group session
  isSolo: true,
  isGroup: false,
  groupId: null,
  participantCount: 1,

  // Voting
  isVotingActive: false,
  voting: null,
  votingQuestionText: null,
  votingChallengeType: null,
  votingChallengeData: null,

  // Group participants
  participants: [] as SessionParticipantInfo[],
  isSessionOwner: false,

  // Journal
  journal: [] as JournalEntryDto[],

  // Revealed POI locations
  revealedLocations: [] as RevealedPoiLocation[],

  // Revealed routes
  revealedRoutes: [] as RevealedRoute[],

  // Linked chat conversation
  conversationId: null,

  // Voting settings
  votingSettings: null,

  // Journal read tracking
  latestJournalSequenceNumber: 0,

  // Proximity
  proximityWarning: null,

  // Clue state
  hasAvailableClues: false,
  cluesUsed: 0,
  totalCluePenaltySeconds: 0,

  // Timer control
  effectiveDuration: null,
  totalPausedSeconds: 0,
  isTimerPaused: false,

  // Completion summary
  completionSummary: null,

  // UI state
  isLoading: false,
  isProcessing: false,
  isSubmitting: false,
  error: null,
  showJournal: false,
  showMap: false,

  // Debug
  lastStateResponse: null,
  lastStateResponseTimestamp: null,
}

// ====================
// STORE
// ====================

export const useSessionStore = create<SessionState>()(
  devtools(
    (set, get) => ({
      ...initialState,

      // Load full session state from API
      // Accepts SessionStateDto or AdventureSessionFullDto (which has participants + votingSettings)
      loadSession: (state) => {
        // Extract POI locations from current node if applicable
        const revealedLocations: RevealedPoiLocation[] = []
        if (state.currentNode?.poiLocation) {
          revealedLocations.push({
            nodeId: state.currentNode.id,
            nodeLabel: state.currentNode.label,
            ...state.currentNode.poiLocation,
            revealedAt: new Date().toISOString(),
          })
        }

        // Merge challenge data into currentNode (support both camelCase and PascalCase from API)
        const qaChallenge = state.questionAnswerChallenge ?? (state as any).QuestionAnswerChallenge
        const numChallenge = state.numpadChallenge ?? (state as any).NumpadChallenge
        const qrChallenge = state.qrCodeChallenge ?? (state as any).QrCodeChallenge ?? (state as any).QRCodeChallenge
        const modChallenge = state.moderatorApprovalChallenge ?? (state as any).ModeratorApprovalChallenge
        const codexChallenge = state.codexChallenge ?? (state as any).CodexChallenge

        let currentNode = state.currentNode ?? (state as any).CurrentNode
        if (currentNode && (state.requiresInput ?? (state as any).RequiresInput)) {
          currentNode = { ...currentNode }
          if (qaChallenge) {
            currentNode.questionText = qaChallenge.questionText ?? qaChallenge.QuestionText
            currentNode.answerType = qaChallenge.answerType ?? qaChallenge.AnswerType
            currentNode.answerOptions = qaChallenge.answerOptions ?? qaChallenge.AnswerOptions ?? undefined
            currentNode.allowSkip = qaChallenge.allowSkip ?? qaChallenge.AllowSkip
            currentNode.skipButtonText = qaChallenge.skipButtonText ?? qaChallenge.SkipButtonText ?? undefined
            currentNode.cancelButtonText = qaChallenge.cancelButtonText ?? qaChallenge.CancelButtonText ?? undefined
          } else if (numChallenge) {
            currentNode.digitCount = numChallenge.digitCount ?? numChallenge.DigitCount
            currentNode.inputStyle = numChallenge.inputStyle ?? numChallenge.InputStyle
            currentNode.allowSkip = numChallenge.allowSkip ?? numChallenge.AllowSkip
            currentNode.skipButtonText = numChallenge.skipButtonText ?? numChallenge.SkipButtonText ?? undefined
            currentNode.cancelButtonText = numChallenge.cancelButtonText ?? numChallenge.CancelButtonText ?? undefined
          } else if (qrChallenge) {
            currentNode.instructions = qrChallenge.instructions ?? qrChallenge.Instructions ?? undefined
            currentNode.allowSkip = qrChallenge.allowSkip ?? qrChallenge.AllowSkip
            currentNode.skipButtonText = qrChallenge.skipButtonText ?? qrChallenge.SkipButtonText ?? undefined
            currentNode.cancelButtonText = qrChallenge.cancelButtonText ?? qrChallenge.CancelButtonText ?? undefined
          } else if (codexChallenge) {
            currentNode.letterCount = codexChallenge.letterCount ?? codexChallenge.LetterCount
            currentNode.allowSkip = codexChallenge.allowSkip ?? codexChallenge.AllowSkip
            currentNode.skipButtonText = codexChallenge.skipButtonText ?? codexChallenge.SkipButtonText ?? undefined
            currentNode.cancelButtonText = codexChallenge.cancelButtonText ?? codexChallenge.CancelButtonText ?? undefined
          } else if (modChallenge) {
            currentNode.instructions = modChallenge.instructions ?? modChallenge.Instructions ?? undefined
            currentNode.allowSkip = modChallenge.allowSkip ?? modChallenge.AllowSkip
            currentNode.skipButtonText = modChallenge.skipButtonText ?? modChallenge.SkipButtonText ?? undefined
            currentNode.cancelButtonText = modChallenge.cancelButtonText ?? modChallenge.CancelButtonText ?? undefined
          }
        }

        // Build pendingInput from challenge data if not provided by API
        let pendingInput = state.pendingInput ?? (state as any).PendingInput
        if (!pendingInput && (state.requiresInput ?? (state as any).RequiresInput) && (state.inputType ?? (state as any).InputType) && (state.currentNodeId ?? (state as any).CurrentNodeId)) {
          const challenge = qaChallenge || numChallenge || qrChallenge || codexChallenge || modChallenge
          pendingInput = {
            nodeId: state.currentNodeId,
            inputType: state.inputType,
            required: true,
            allowSkip: challenge?.allowSkip ?? false,
            allowCancel: false,
            skipButtonText: challenge?.skipButtonText || undefined,
            cancelButtonText: challenge?.cancelButtonText || undefined,
          }
        }

        // Support both camelCase and PascalCase from API (C# backend may use either)
        const s = state as any

        set({
          sessionId: state.sessionId ?? s.SessionId ?? s.id ?? s.Id,
          adventureId: state.adventureId ?? s.AdventureId,
          adventureTitle: state.adventureTitle ?? s.AdventureTitle,
          status: state.status ?? s.Status,
          currentNodeId: state.currentNodeId ?? s.CurrentNodeId,
          currentNode,
          currentNodeStatus: state.currentNodeStatus ?? s.CurrentNodeStatus,
          cooldownEndsAt: state.cooldownEndsAt ?? s.CooldownEndsAt,
          waitingReason: state.waitingReason ?? s.WaitingReason,
          requiresInput: state.requiresInput ?? s.RequiresInput,
          inputType: state.inputType || s.InputType || null,
          pendingInput,
          progressPercent: state.progressPercent ?? s.ProgressPercent ?? 0,
          startedAt: state.startedAt ?? s.StartedAt,
          completedAt: state.completedAt ?? s.CompletedAt,
          lastActivityAt: state.lastActivityAt ?? s.LastActivityAt,
          elapsedSeconds: state.elapsedSeconds ?? s.ElapsedSeconds ?? 0,
          isSolo: state.isSolo ?? s.IsSolo,
          isGroup: state.isGroup ?? s.IsGroup,
          groupId: state.groupId ?? s.GroupId,
          participantCount: state.participantCount ?? s.ParticipantCount ?? ((state as any).participants ?? (state as any).Participants)?.length ?? 1,
          isVotingActive: state.isVotingActive ?? s.IsVotingActive,
          voting: normalizeVotingRound(
            state.activeVotingRound ?? s.ActiveVotingRound,
            qaChallenge?.answerOptions ?? qaChallenge?.AnswerOptions,
          ),
          votingQuestionText: qaChallenge?.questionText ?? qaChallenge?.QuestionText
            ?? numChallenge?.questionText ?? numChallenge?.QuestionText
            ?? codexChallenge?.questionText ?? codexChallenge?.QuestionText
            ?? null,
          votingChallengeType: (state.inputType ?? s.InputType) || null,
          votingChallengeData: (qaChallenge || numChallenge || qrChallenge || codexChallenge || modChallenge) as Record<string, unknown> | null,
          journal: [], // Clear old journal when loading new session
          revealedLocations,
          revealedRoutes: [],
          // Use participants from full DTO if available (e.g. from getSession)
          participants: (state as any).participants ?? (state as any).Participants ?? [],
          isSessionOwner: false,
          hasAvailableClues: state.hasAvailableClues ?? s.HasAvailableClues ?? false,
          conversationId: state.conversationId ?? s.ConversationId ?? null,
          votingSettings: state.votingSettings ?? s.VotingSettings ?? null,
          latestJournalSequenceNumber: state.latestJournalSequenceNumber ?? s.LatestJournalSequenceNumber ?? 0,
          proximityWarning: state.proximityWarning ?? s.ProximityWarning ?? null,
          effectiveDuration: state.effectiveDuration ?? s.EffectiveDuration ?? null,
          totalPausedSeconds: state.totalPausedSeconds ?? s.TotalPausedSeconds ?? 0,
          isTimerPaused: state.isTimerPaused ?? s.IsTimerPaused ?? false,
          isLoading: false,
          error: null,
        }, false, 'loadSession')
      },

      // Update partial session state (for polling updates)
      updateSessionState: (state) => {
        const current = get()
        const updates: Partial<SessionState> = {}
        // Support both camelCase and PascalCase from API
        const s = state as any

        if ((state.sessionId ?? s.SessionId) !== undefined) updates.sessionId = state.sessionId ?? s.SessionId
        if ((state.adventureId ?? s.AdventureId) !== undefined) updates.adventureId = state.adventureId ?? s.AdventureId
        if ((state.adventureTitle ?? s.AdventureTitle) !== undefined) updates.adventureTitle = state.adventureTitle ?? s.AdventureTitle
        if ((state.status ?? s.Status) !== undefined) updates.status = state.status ?? s.Status
        if ((state.currentNodeId ?? s.CurrentNodeId) !== undefined) updates.currentNodeId = state.currentNodeId ?? s.CurrentNodeId
        // Declare at function scope so pendingInput building (below) can access them
        const uQaChallenge = state.questionAnswerChallenge ?? s.QuestionAnswerChallenge
        const uNumChallenge = state.numpadChallenge ?? s.NumpadChallenge
        const uQrChallenge = state.qrCodeChallenge ?? s.QrCodeChallenge ?? s.QRCodeChallenge
        const uModChallenge = state.moderatorApprovalChallenge ?? s.ModeratorApprovalChallenge
        const uCodexChallenge = state.codexChallenge ?? s.CodexChallenge

        const currentNodeVal = state.currentNode ?? s.CurrentNode
        if (currentNodeVal !== undefined) {

          let currentNode = currentNodeVal
          if (currentNode && (state.requiresInput ?? s.RequiresInput)) {
            currentNode = { ...currentNode }
            if (uQaChallenge) {
              currentNode.questionText = uQaChallenge.questionText ?? uQaChallenge.QuestionText
              currentNode.answerType = uQaChallenge.answerType ?? uQaChallenge.AnswerType
              currentNode.answerOptions = uQaChallenge.answerOptions ?? uQaChallenge.AnswerOptions ?? undefined
              currentNode.allowSkip = uQaChallenge.allowSkip ?? uQaChallenge.AllowSkip
              currentNode.skipButtonText = uQaChallenge.skipButtonText ?? uQaChallenge.SkipButtonText ?? undefined
              currentNode.cancelButtonText = uQaChallenge.cancelButtonText ?? uQaChallenge.CancelButtonText ?? undefined
            } else if (uNumChallenge) {
              currentNode.digitCount = uNumChallenge.digitCount ?? uNumChallenge.DigitCount
              currentNode.inputStyle = uNumChallenge.inputStyle ?? uNumChallenge.InputStyle
              currentNode.allowSkip = uNumChallenge.allowSkip ?? uNumChallenge.AllowSkip
              currentNode.skipButtonText = uNumChallenge.skipButtonText ?? uNumChallenge.SkipButtonText ?? undefined
              currentNode.cancelButtonText = uNumChallenge.cancelButtonText ?? uNumChallenge.CancelButtonText ?? undefined
            } else if (uQrChallenge) {
              currentNode.instructions = uQrChallenge.instructions ?? uQrChallenge.Instructions ?? undefined
              currentNode.allowSkip = uQrChallenge.allowSkip ?? uQrChallenge.AllowSkip
              currentNode.skipButtonText = uQrChallenge.skipButtonText ?? uQrChallenge.SkipButtonText ?? undefined
              currentNode.cancelButtonText = uQrChallenge.cancelButtonText ?? uQrChallenge.CancelButtonText ?? undefined
            } else if (uCodexChallenge) {
              currentNode.letterCount = uCodexChallenge.letterCount ?? uCodexChallenge.LetterCount
              currentNode.allowSkip = uCodexChallenge.allowSkip ?? uCodexChallenge.AllowSkip
              currentNode.skipButtonText = uCodexChallenge.skipButtonText ?? uCodexChallenge.SkipButtonText ?? undefined
              currentNode.cancelButtonText = uCodexChallenge.cancelButtonText ?? uCodexChallenge.CancelButtonText ?? undefined
            } else if (uModChallenge) {
              currentNode.instructions = uModChallenge.instructions ?? uModChallenge.Instructions ?? undefined
              currentNode.allowSkip = uModChallenge.allowSkip ?? uModChallenge.AllowSkip
              currentNode.skipButtonText = uModChallenge.skipButtonText ?? uModChallenge.SkipButtonText ?? undefined
              currentNode.cancelButtonText = uModChallenge.cancelButtonText ?? uModChallenge.CancelButtonText ?? undefined
            }
          }
          updates.currentNode = currentNode
          // Auto-extract POI location if present and not already revealed
          if (currentNode?.poiLocation) {
            const alreadyRevealed = current.revealedLocations.some(
              loc => loc.nodeId === currentNode!.id
            )
            if (!alreadyRevealed) {
              updates.revealedLocations = [
                ...current.revealedLocations,
                {
                  nodeId: currentNode.id,
                  nodeLabel: currentNode.label,
                  ...currentNode.poiLocation,
                  revealedAt: new Date().toISOString(),
                },
              ]
            }
          }
        }
        if ((state.currentNodeStatus ?? s.CurrentNodeStatus) !== undefined) updates.currentNodeStatus = state.currentNodeStatus ?? s.CurrentNodeStatus
        if ((state.cooldownEndsAt ?? s.CooldownEndsAt) !== undefined) updates.cooldownEndsAt = state.cooldownEndsAt ?? s.CooldownEndsAt
        if ((state.waitingReason ?? s.WaitingReason) !== undefined) updates.waitingReason = state.waitingReason ?? s.WaitingReason
        if ((state.requiresInput ?? s.RequiresInput) !== undefined) updates.requiresInput = state.requiresInput ?? s.RequiresInput
        if ((state.inputType ?? s.InputType) !== undefined) updates.inputType = state.inputType || s.InputType || null

        // Build pendingInput from challenge data if not provided
        const pendingInputVal = state.pendingInput ?? s.PendingInput
        if (pendingInputVal !== undefined) {
          updates.pendingInput = pendingInputVal
        } else if ((state.requiresInput ?? s.RequiresInput) && (state.inputType ?? s.InputType) && (state.currentNodeId ?? s.CurrentNodeId)) {
          const challenge = uQaChallenge || uNumChallenge || uQrChallenge || uCodexChallenge || uModChallenge
          updates.pendingInput = {
            nodeId: state.currentNodeId ?? s.CurrentNodeId,
            inputType: state.inputType ?? s.InputType,
            required: true,
            allowSkip: challenge?.allowSkip ?? challenge?.AllowSkip ?? false,
            allowCancel: false,
            skipButtonText: challenge?.skipButtonText ?? challenge?.SkipButtonText ?? undefined,
            cancelButtonText: challenge?.cancelButtonText ?? challenge?.CancelButtonText ?? undefined,
          }
        }

        if ((state.progressPercent ?? s.ProgressPercent) !== undefined) updates.progressPercent = state.progressPercent ?? s.ProgressPercent
        if ((state.startedAt ?? s.StartedAt) !== undefined) updates.startedAt = state.startedAt ?? s.StartedAt
        if ((state.completedAt ?? s.CompletedAt) !== undefined) updates.completedAt = state.completedAt ?? s.CompletedAt
        if ((state.lastActivityAt ?? s.LastActivityAt) !== undefined) updates.lastActivityAt = state.lastActivityAt ?? s.LastActivityAt
        if ((state.elapsedSeconds ?? s.ElapsedSeconds) !== undefined) updates.elapsedSeconds = state.elapsedSeconds ?? s.ElapsedSeconds
        if ((state.isSolo ?? s.IsSolo) !== undefined) updates.isSolo = state.isSolo ?? s.IsSolo
        if ((state.isGroup ?? s.IsGroup) !== undefined) updates.isGroup = state.isGroup ?? s.IsGroup
        if ((state.groupId ?? s.GroupId) !== undefined) updates.groupId = state.groupId ?? s.GroupId
        if ((state.participantCount ?? s.ParticipantCount) !== undefined) updates.participantCount = state.participantCount ?? s.ParticipantCount
        if ((state.isVotingActive ?? s.IsVotingActive) !== undefined) updates.isVotingActive = state.isVotingActive ?? s.IsVotingActive
        if ((state.hasAvailableClues ?? s.HasAvailableClues) !== undefined) updates.hasAvailableClues = state.hasAvailableClues ?? s.HasAvailableClues
        if ((state.conversationId ?? s.ConversationId) !== undefined) updates.conversationId = state.conversationId ?? s.ConversationId ?? null
        if ((state.votingSettings ?? s.VotingSettings) !== undefined) updates.votingSettings = state.votingSettings ?? s.VotingSettings ?? null
        if ((state.latestJournalSequenceNumber ?? s.LatestJournalSequenceNumber) !== undefined) updates.latestJournalSequenceNumber = state.latestJournalSequenceNumber ?? s.LatestJournalSequenceNumber ?? 0
        if ((state.proximityWarning ?? s.ProximityWarning) !== undefined) updates.proximityWarning = state.proximityWarning ?? s.ProximityWarning ?? null
        if ((state.activeVotingRound ?? s.ActiveVotingRound) !== undefined) {
          updates.voting = normalizeVotingRound(
            state.activeVotingRound ?? s.ActiveVotingRound,
            uQaChallenge?.answerOptions ?? uQaChallenge?.AnswerOptions ?? current.voting?.options?.map(o => ({ id: o.id, text: o.label })),
          )
          // Only overwrite challenge data if new challenge data is present in this update;
          // otherwise preserve existing values (polls may omit challenge details).
          // If voting round is null/cleared, also clear challenge data.
          const rawRound = state.activeVotingRound ?? s.ActiveVotingRound
          const newChallengeData = uQaChallenge || uNumChallenge || uQrChallenge || uCodexChallenge || uModChallenge
          if (newChallengeData) {
            updates.votingQuestionText = uQaChallenge?.questionText ?? uQaChallenge?.QuestionText
              ?? uNumChallenge?.questionText ?? uNumChallenge?.QuestionText
              ?? uCodexChallenge?.questionText ?? uCodexChallenge?.QuestionText
              ?? null
            updates.votingChallengeData = newChallengeData as Record<string, unknown>
          } else if (!rawRound) {
            // Voting ended, clear challenge data
            updates.votingQuestionText = null
            updates.votingChallengeType = null
            updates.votingChallengeData = null
          }
          const newInputType = (state.inputType ?? s.InputType)
          if (newInputType) {
            updates.votingChallengeType = newInputType
          }
        }
        if ((state.effectiveDuration ?? s.EffectiveDuration) !== undefined) updates.effectiveDuration = state.effectiveDuration ?? s.EffectiveDuration ?? null
        if ((state.totalPausedSeconds ?? s.TotalPausedSeconds) !== undefined) updates.totalPausedSeconds = state.totalPausedSeconds ?? s.TotalPausedSeconds
        if ((state.isTimerPaused ?? s.IsTimerPaused) !== undefined) updates.isTimerPaused = state.isTimerPaused ?? s.IsTimerPaused

        set(updates, false, 'updateSessionState')
      },

      setCurrentNode: (node) => {
        const current = get()
        const updates: Partial<SessionState> = {
          currentNode: node,
          currentNodeId: node?.id || null,
        }

        // Auto-extract POI location if present
        if (node?.poiLocation) {
          const alreadyRevealed = current.revealedLocations.some(
            loc => loc.nodeId === node.id
          )
          if (!alreadyRevealed) {
            updates.revealedLocations = [
              ...current.revealedLocations,
              {
                nodeId: node.id,
                nodeLabel: node.label,
                ...node.poiLocation,
                revealedAt: new Date().toISOString(),
              },
            ]
          }
        }

        set(updates, false, 'setCurrentNode')
      },

      setPendingInput: (input) => set({
        pendingInput: input,
        requiresInput: input !== null,
        inputType: input?.inputType || null,
      }, false, 'setPendingInput'),

      setStatus: (status) => set({ status }, false, 'setStatus'),

      // Journal actions
      setJournal: (entries) => set({ journal: entries }, false, 'setJournal'),

      addJournalEntry: (entry) => set((state) => ({
        journal: [...state.journal, entry],
      }), false, 'addJournalEntry'),

      addJournalEntries: (entries) => set((state) => ({
        journal: [...state.journal, ...entries],
      }), false, 'addJournalEntries'),

      // Map actions
      addRevealedLocation: (location) => set((state) => {
        // Don't add duplicates
        if (state.revealedLocations.some(loc => loc.nodeId === location.nodeId)) {
          return state
        }
        return { revealedLocations: [...state.revealedLocations, location] }
      }, false, 'addRevealedLocation'),

      clearRevealedLocations: () => set({ revealedLocations: [] }, false, 'clearRevealedLocations'),

      addRevealedRoute: (route) => set((state) => {
        // Don't add duplicates
        if (state.revealedRoutes.some(r => r.nodeId === route.nodeId)) {
          return state
        }
        return { revealedRoutes: [...state.revealedRoutes, route] }
      }, false, 'addRevealedRoute'),

      clearRevealedRoutes: () => set({ revealedRoutes: [] }, false, 'clearRevealedRoutes'),

      // Participant actions
      setParticipants: (participants) => set({ participants }, false, 'setParticipants'),

      addParticipant: (participant) => set((state) => {
        if (state.participants.some(p => p.userId === participant.userId)) {
          return state
        }
        return { participants: [...state.participants, participant] }
      }, false, 'addParticipant'),

      removeParticipant: (userId) => set((state) => ({
        participants: state.participants.filter(p => p.userId !== userId),
      }), false, 'removeParticipant'),

      updateParticipantOnlineStatus: (userId, isOnline) => set((state) => ({
        participants: state.participants.map(p =>
          p.userId === userId ? { ...p, isOnline } : p
        ),
      }), false, 'updateParticipantOnlineStatus'),

      updateParticipantReady: (userId, isReady, readyAt) => set((state) => ({
        participants: state.participants.map(p =>
          p.userId === userId ? { ...p, isReady, readyAt } : p
        ),
      }), false, 'updateParticipantReady'),

      setIsSessionOwner: (isOwner) => set({ isSessionOwner: isOwner }, false, 'setIsSessionOwner'),

      // Voting actions
      setVoting: (voting) => set({
        voting,
        isVotingActive: voting !== null && voting.status === 'Open',
      }, false, 'setVoting'),

      updateVotingProgress: (votesCast, percentageVoted, isComplete) => set((state) => {
        if (!state.voting) return state
        return {
          voting: {
            ...state.voting,
            votesCast,
            percentageVoted,
            ...(isComplete !== undefined && { isComplete }),
          },
        }
      }, false, 'updateVotingProgress'),

      // Completion summary
      setCompletionSummary: (summary) => set({ completionSummary: summary }, false, 'setCompletionSummary'),

      // Clue actions
      setClueState: (hasAvailable, used, penalty) => set({
        hasAvailableClues: hasAvailable,
        cluesUsed: used,
        totalCluePenaltySeconds: penalty,
      }, false, 'setClueState'),

      // UI actions
      setLoading: (loading) => set({ isLoading: loading }, false, 'setLoading'),
      setProcessing: (processing) => set({ isProcessing: processing }, false, 'setProcessing'),
      setSubmitting: (submitting) => set({ isSubmitting: submitting }, false, 'setSubmitting'),
      setError: (error) => set({ error }, false, 'setError'),

      toggleJournal: () => set((state) => ({
        showJournal: !state.showJournal,
      }), false, 'toggleJournal'),

      toggleMap: () => set((state) => ({
        showMap: !state.showMap,
      }), false, 'toggleMap'),

      setShowJournal: (show) => set({ showJournal: show }, false, 'setShowJournal'),
      setShowMap: (show) => set({ showMap: show }, false, 'setShowMap'),

      // Debug actions
      setLastStateResponse: (response) => set({
        lastStateResponse: response,
        lastStateResponseTimestamp: new Date().toISOString(),
      }, false, 'setLastStateResponse'),

      // Reset
      reset: () => set(initialState, false, 'reset'),
    }),
    { name: 'SessionStore' }
  )
)
